import apiClient from '@/lib/apiClient';
import {
  ApiResponse,
  EmailTemplate,
  EmailTemplateCategory,
  EmailVariableDefinition,
} from '@/types';

export interface EmailTemplateListFilters {
  category?: EmailTemplateCategory;
  department?: string;
  isActive?: boolean;
}

export type EmailTemplatePayload = Omit<
  EmailTemplate,
  'id' | 'hasBeenUsed' | 'createdBy' | 'createdAt' | 'updatedAt'
>;

export const emailTemplateService = {
  getAll: async (filters?: EmailTemplateListFilters): Promise<EmailTemplate[]> => {
    const { data } = await apiClient.get<ApiResponse<EmailTemplate[]>>('/email-templates', {
      params: {
        ...filters,
        isActive: filters?.isActive === undefined ? undefined : String(filters.isActive),
      },
    });
    return data.data ?? [];
  },

  getById: async (id: number): Promise<EmailTemplate> => {
    const { data } = await apiClient.get<ApiResponse<EmailTemplate>>(`/email-templates/${id}`);
    if (!data.data) throw new Error('Template not found in response');
    return data.data;
  },

  getVariables: async (): Promise<EmailVariableDefinition[]> => {
    const { data } = await apiClient.get<ApiResponse<EmailVariableDefinition[]>>(
      '/email-templates/variables',
    );
    return data.data ?? [];
  },

  create: async (payload: EmailTemplatePayload): Promise<EmailTemplate> => {
    const { data } = await apiClient.post<ApiResponse<EmailTemplate>>('/email-templates', payload);
    if (!data.data) throw new Error('Template not found in response');
    return data.data;
  },

  update: async (id: number, payload: Partial<EmailTemplatePayload>): Promise<EmailTemplate> => {
    const { data } = await apiClient.put<ApiResponse<EmailTemplate>>(
      `/email-templates/${id}`,
      payload,
    );
    if (!data.data) throw new Error('Template not found in response');
    return data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/email-templates/${id}`);
  },
};
